import { Link } from '../../primitives/Link/Link';
import type {
  KeyboardEventHandler,
  MouseEventHandler,
  PointerEventHandler,
  ReactNode,
  Ref,
} from 'react';
import styles from './Button.module.css';

/**
 * Figma: `Buttons` (86:2267) — "Button component with 5 hierarchy levels
 * (Primary, Secondary, Tertiary, Ghost Grey, Ghost Color), 3 interaction states
 * (Default, Hover, Disabled), and an icon-only variant. Supports leading and
 * trailing icons via instance swap. Uses Inter Medium 14px across all variants."
 *
 * `Hierarchy=Destructive` (2024:20177) joined the set with the Modal work — a
 * sixth, solid hierarchy with Primary's geometry, drawn for `Modal`'s
 * destructive confirm. The set's description above predates it.
 *
 * The 27 variants come from three variant properties — `Hierarchy`, `State` and
 * `Icon only` — plus two boolean toggles and two instance-swap slots for the
 * icons. Only `Hierarchy` becomes an enum prop: `State` is real interaction
 * state, and `Icon only` is implied by whether a label was passed.
 */
export type ButtonHierarchy =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'ghostGrey'
  | 'ghostColor'
  | 'destructive';

/**
 * Force a state for documentation and visual regression only. Hover is CSS in
 * the real component, and disabled is the `disabled` prop, which is real state.
 */
export type ButtonState = 'default' | 'hover';

type ButtonBaseProps = {
  /** Figma `Hierarchy`. */
  hierarchy?: ButtonHierarchy;
  /**
   * Figma `⬅️ Icon leading` (toggle) + `🔀 Icon leading swap` (instance swap).
   * Omit to render without a leading icon. On an icon-only button this is the
   * icon.
   */
  iconLeading?: ReactNode;
  /** Figma `➡️ Icon trailing` + `🔀 Icon trailing swap`. */
  iconTrailing?: ReactNode;
  /** Figma `State=Disabled`. */
  disabled?: boolean;
  /** Renders an `<a>` instead of a `<button>`. Not in Figma — see the summary. */
  href?: string;
  type?: 'button' | 'submit' | 'reset';
  onClick?: MouseEventHandler<HTMLElement>;
  state?: ButtonState;
  className?: string;
  /**
   * Marks a toggle button as on or off. Not in Figma — Figma has no concept of
   * an accessible state, and the file draws no pressed variant either. Added for
   * `MessageActions`, whose thumbs are toggles; without it a button that holds a
   * state can only report the state by colour, which assistive tech cannot see.
   *
   * Ignored on the `<a>` branch: a link is not a toggle.
   */
  'aria-pressed'?: boolean;
  /** Roving-tabindex hosts (`role="toolbar"`) take every button but one out of the tab order. */
  tabIndex?: number;
  /**
   * Native tooltip. Not in Figma, which has no hover-text concept. `aria-label`
   * names the control for assistive tech; this is what a sighted pointer user
   * gets, and on an icon-only button it is the only label there is.
   */
  title?: string;
  /**
   * Disclosure wiring, for a button that opens an anchored panel — `Dropdown`'s
   * trigger, and any menu or popover trigger after it. Not in Figma, which has
   * no concept of an accessible state.
   *
   * Ignored on the `<a>` branch: a link does not own a popup.
   */
  'aria-haspopup'?: 'menu' | 'listbox' | 'dialog' | 'grid' | 'tree' | true;
  'aria-expanded'?: boolean;
  'aria-controls'?: string;
  /**
   * So a disclosure trigger can open its panel on ArrowDown and move focus into
   * it. Not in Figma.
   */
  onKeyDown?: KeyboardEventHandler<HTMLElement>;
  /**
   * So a drag handle can start a drag on the press rather than on the click.
   * Not in Figma, which defines appearance and not gestures.
   *
   * Same category as `onKeyDown` above: a curated escape hatch rather than a
   * spread, added because `Card Header`'s `Type=Draggable` handle (996:30988)
   * is a real `Buttons/Button` and a pointer-driven reorder has to hear the
   * press. `onClick` is too late — by then the pointer has already been
   * released, which is the end of a drag, not its start.
   */
  onPointerDown?: PointerEventHandler<HTMLElement>;
  /**
   * Points a control at the element describing how to operate it. Not in
   * Figma. A drag handle needs it: "press Space to pick up" is instructions,
   * not a name, so it cannot go in `aria-label`.
   */
  'aria-describedby'?: string;
  /** So a trigger's owner can return focus to it after the panel closes. */
  ref?: Ref<HTMLButtonElement>;
};

/**
 * An icon-only button (Figma `Icon only=True`) has no text to name it, so
 * `aria-label` is required rather than optional. Not in Figma — Figma has no
 * concept of an accessible name.
 */
export type ButtonProps =
  | (ButtonBaseProps & { children: ReactNode; 'aria-label'?: string })
  | (ButtonBaseProps & { children?: undefined; 'aria-label': string });

const hierarchyClass: Record<ButtonHierarchy, string> = {
  primary: styles.primary,
  secondary: styles.secondary,
  tertiary: styles.tertiary,
  ghostGrey: styles.ghostGrey,
  ghostColor: styles.ghostColor,
  destructive: styles.destructive,
};

/** Ghost hierarchies have no container — no fill, stroke, shadow or vertical padding. */
const isGhost = (hierarchy: ButtonHierarchy) =>
  hierarchy === 'ghostGrey' || hierarchy === 'ghostColor';

export function Button({
  hierarchy = 'primary',
  iconLeading,
  iconTrailing,
  disabled = false,
  href,
  type = 'button',
  onClick,
  state,
  className,
  children,
  'aria-label': ariaLabel,
  'aria-pressed': ariaPressed,
  'aria-haspopup': ariaHasPopup,
  'aria-expanded': ariaExpanded,
  'aria-controls': ariaControls,
  onKeyDown,
  onPointerDown,
  'aria-describedby': ariaDescribedBy,
  tabIndex,
  title,
  ref,
}: ButtonProps) {
  const iconOnly = children === undefined;

  const classes = [
    styles.button,
    isGhost(hierarchy) ? styles.ghost : styles.solid,
    iconOnly ? styles.iconOnly : null,
    hierarchyClass[hierarchy],
    disabled ? styles.disabled : null,
    /*
     * Hover is live only when no state is forced and the button can be pressed,
     * so `:hover` reacts in the app and stays inert in a states story. A forced
     * hover on a disabled button is ignored — Figma has no such variant.
     */
    !disabled && state === 'hover' ? styles.hover : null,
    !disabled && state === undefined ? styles.interactive : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const content = (
    <>
      {iconLeading !== undefined && <span className={styles.iconSlot}>{iconLeading}</span>}
      {!iconOnly && <span className={styles.label}>{children}</span>}
      {iconTrailing !== undefined && <span className={styles.iconSlot}>{iconTrailing}</span>}
    </>
  );

  /*
   * A link with an href is an `<a>`, but a *disabled* link is not a thing the
   * platform can express — `<a>` ignores `disabled` and stays focusable and
   * followable. Fall back to a real disabled `<button>` so the state is honest
   * to the keyboard and to assistive tech. Not in Figma.
   */
  if (href !== undefined && !disabled) {
    return (
      <Link className={classes} href={href} onClick={onClick} aria-label={ariaLabel} title={title}>
        {content}
      </Link>
    );
  }

  return (
    <button
      className={classes}
      type={type}
      disabled={disabled}
      onClick={onClick}
      aria-label={ariaLabel}
      aria-pressed={ariaPressed}
      aria-haspopup={ariaHasPopup}
      aria-expanded={ariaExpanded}
      aria-controls={ariaControls}
      onKeyDown={onKeyDown}
      onPointerDown={onPointerDown}
      aria-describedby={ariaDescribedBy}
      tabIndex={tabIndex}
      title={title}
      ref={ref}
    >
      {content}
    </button>
  );
}
